const MIN_LINE_SIZE = 12;
const MAX_LINE_SIZE = 40;
const DEFAULT_LINE_SIZE = 18;
const PREVIEW_PADDING_PX = 36;
const DRAG_SENSITIVITY = 2;

class CardConfigurator extends HTMLElement {
  connectedCallback() {
    this.kaartjeVariantId = this.dataset.kaartjeVariantId;
    this.productTitle = this.dataset.productTitle || '';
    this.propertyDesign = this.dataset.propertyDesign;
    this.propertyMessage = this.dataset.propertyMessage;
    this.propertyFont = this.dataset.propertyFont;
    this.propertyLineSizes = this.dataset.propertyLineSizes;
    this.propertyLineColors = this.dataset.propertyLineColors;
    this.propertyLineRotations = this.dataset.propertyLineRotations;
    this.propertyVerticalOffset = this.dataset.propertyVerticalOffset;
    this.propertyPreviewImage = this.dataset.propertyPreviewImage || 'Kaartje voorbeeld';
    this.propertyLink = this.dataset.propertyLink;
    this.mainMarkProperty = this.dataset.mainMarkProperty;
    this.mainMarkValue = this.dataset.mainMarkValue;
    this.errorText = this.dataset.errorText;
    this.formId = this.dataset.formId;

    this.choiceMet = this.querySelector('[data-kaartje-choice="met"]');
    this.choiceZonder = this.querySelector('[data-kaartje-choice="zonder"]');
    this.choiceInputs = Array.from(this.querySelectorAll('[data-kaartje-choice]'));
    this.dialog = this.querySelector('[ref="dialog"]');
    this.errorEl = this.querySelector('[data-error]');
    this.designErrorEl = this.querySelector('[data-error-step1]');
    this.stepIndicator = this.querySelector('[data-step-indicator]');
    this.step1 = this.querySelector('[data-step="1"]');
    this.step2 = this.querySelector('[data-step="2"]');
    this.designRadios = Array.from(this.querySelectorAll('[data-design-radio]'));
    this.fontSelect = this.querySelector('[data-font-select]');
    this.previewLines = this.querySelector('[data-preview-lines]');
    this.previewCard = this.querySelector('.card-configurator__preview-card');
    this.overflowWarning = this.querySelector('[data-overflow-warning]');
    this.lineToolbar = this.querySelector('[data-line-toolbar]');
    this.colorPicker = this.querySelector('[data-color-picker]');
    this.moveHandle = this.querySelector('[data-move-handle]');
    this.centerGuide = this.querySelector('[data-center-guide]');
    this.placeholderText = this.previewLines.dataset.placeholderText || '';

    this.overflowing = false;
    this.configured = false;
    this.activeLineWrapper = null;
    this.verticalOffset = 0;

    this.moveHandle.addEventListener('pointerdown', (event) => this.handleMoveDrag(event));

    this.colorPicker.addEventListener('input', () => {
      if (!this.activeLineWrapper) return;
      const editable = this.activeLineWrapper.querySelector('.card-configurator__line-text');
      editable.dataset.color = this.colorPicker.value;
      editable.style.color = this.colorPicker.value;
    });

    this.previewCard.addEventListener('pointerdown', (event) => {
      const isInsideLine = event.target.closest('.card-configurator__line');
      const isToolbar = event.target.closest('.card-configurator__line-toolbar');
      if (!isInsideLine && !isToolbar) {
        this.setActiveLine(null);
      }
    });

    this.fontSelect.addEventListener('change', () => {
      this.applyFontToAllLines();
      this.checkOverflow();
    });

    this.ensureAtLeastOneLine();

    this.form = this.resolveForm();
    this.submitButton = this.form
      ? this.form.querySelector('button[type="submit"], [name="add"]')
      : null;

    this.querySelector('[data-action="close"]').addEventListener('click', () => {
      this.dialog.close();
    });

    // Sluit de klant de popup zonder te bevestigen (kruisje of Escape), dan
    // zetten we de keuze terug op "Zonder kaartje" — er is immers geen kaartje
    // gekozen.
    this.dialog.addEventListener('close', () => {
      if (!this.configured && this.choiceZonder) {
        this.choiceZonder.checked = true;
      }
    });

    this.querySelector('[data-action="next"]').addEventListener('click', () => {
      this.handleNext();
    });

    this.querySelector('[data-action="back"]').addEventListener('click', () => {
      this.goToStep(1);
    });

    this.querySelector('[data-action="confirm"]').addEventListener('click', () => {
      this.handleConfirm();
    });

    // De klant kiest zelf via de knoppen of er een kaartje bij moet. Kiest hij
    // "Met kaartje", dan opent de popup. Kiest hij "Zonder kaartje", dan
    // vervalt een eventueel eerder ingevuld ontwerp weer.
    this.choiceInputs.forEach((input) =>
      input.addEventListener('change', () => this.handleChoiceChange())
    );

    if (this.submitButton) {
      this.submitButton.addEventListener('click', (event) => this.handleSubmitAttempt(event));
    }
  }

  resolveForm() {
    if (this.formId) {
      const byId = document.getElementById(this.formId);
      if (byId) return byId;
    }
    return document.querySelector('form[data-type="add-to-cart-form"]');
  }

  isKaartjeChosen() {
    return !!this.choiceMet && this.choiceMet.checked;
  }

  handleChoiceChange() {
    if (this.isKaartjeChosen()) {
      // "Met kaartje" gekozen: eerst een eventueel eerder ingevuld ontwerp
      // resetten, dan de popup openen.
      this.configured = false;
      this.openDialog();
    } else {
      // "Zonder kaartje" gekozen: markering op het hoofdproduct weghalen zodat
      // er straks een schoon hoofdproduct wordt toegevoegd.
      this.configured = false;
      this.removeMainMark();
    }
  }

  handleSubmitAttempt(event) {
    // Als de klant een kaartje wil maar het ontwerp nog niet heeft bevestigd,
    // onderscheppen we de "toevoegen aan winkelwagen"-klik en openen we eerst
    // de popup.
    if (this.isKaartjeChosen() && !this.configured) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.openDialog();
    }
  }

  openDialog() {
    this.goToStep(1);
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    this.errorEl.hidden = true;
    this.designErrorEl.hidden = true;
  }

  goToStep(stepNumber) {
    this.currentStep = stepNumber;
    this.step1.hidden = stepNumber !== 1;
    this.step2.hidden = stepNumber !== 2;
    this.stepIndicator.textContent =
      stepNumber === 1 ? 'Stap 1 van 2 — kies je kaartje' : 'Stap 2 van 2 — ontwerp de achterkant';
    if (stepNumber === 2) {
      this.checkOverflow();
    }
  }

  handleNext() {
    const selectedDesign = this.designRadios.find((radio) => radio.checked);
    if (!selectedDesign) {
      this.designErrorEl.hidden = false;
      return;
    }
    this.designErrorEl.hidden = true;
    this.goToStep(2);
  }

  // --- Direct-op-de-kaart tekst bewerken ---

  ensureAtLeastOneLine() {
    if (this.previewLines.children.length === 0) {
      const wrapper = this.createLine('', DEFAULT_LINE_SIZE);
      wrapper.querySelector('.card-configurator__line-text').dataset.placeholder = this.placeholderText;
      this.previewLines.appendChild(wrapper);
    }
  }

  createLine(text, size) {
    const wrapper = document.createElement('div');
    wrapper.className = 'card-configurator__line';

    const editable = document.createElement('div');
    editable.className = 'card-configurator__line-text';
    editable.contentEditable = 'true';
    editable.spellcheck = false;
    editable.dataset.size = String(size);
    editable.dataset.color = '#2c2c2a';
    editable.dataset.rotation = '0';
    editable.style.fontSize = `${size}px`;
    editable.style.color = editable.dataset.color;
    editable.textContent = text;
    this.applyFontToLine(editable);

    editable.addEventListener('input', () => this.checkOverflow());
    editable.addEventListener('keydown', (event) => this.handleLineKeydown(event, editable));
    editable.addEventListener('paste', (event) => this.handleLinePaste(event));
    editable.addEventListener('focus', () => this.setActiveLine(wrapper));
    editable.addEventListener(
      'wheel',
      (event) => this.handleLineWheel(event, editable),
      { passive: false }
    );

    wrapper.appendChild(editable);

    ['nw', 'ne', 'sw', 'se'].forEach((corner) => {
      const handle = document.createElement('span');
      handle.className = `card-configurator__corner-handle ${corner}`;
      handle.setAttribute('aria-hidden', 'true');
      handle.addEventListener('pointerdown', (event) => this.handleCornerDrag(event, editable));
      wrapper.appendChild(handle);
    });

    const rotateHandle = document.createElement('span');
    rotateHandle.className = 'card-configurator__rotate-handle';
    rotateHandle.setAttribute('aria-hidden', 'true');
    rotateHandle.addEventListener('pointerdown', (event) => this.handleRotateDrag(event, wrapper, editable));
    wrapper.appendChild(rotateHandle);

    return wrapper;
  }

  setActiveLine(wrapper) {
    if (this.activeLineWrapper) {
      this.activeLineWrapper.classList.remove('is-active');
    }
    this.activeLineWrapper = wrapper;
    if (wrapper) {
      wrapper.classList.add('is-active');
      const editable = wrapper.querySelector('.card-configurator__line-text');
      this.colorPicker.value = editable.dataset.color || '#2c2c2a';
      this.lineToolbar.hidden = false;
      this.previewCard.classList.add('has-selection');
    } else {
      this.lineToolbar.hidden = true;
      this.previewCard.classList.remove('has-selection');
    }
  }

  handleMoveDrag(event) {
    event.preventDefault();
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    const startY = event.clientY;
    const startOffset = this.verticalOffset;
    const maxOffset = Math.max(0, (this.previewCard.clientHeight - PREVIEW_PADDING_PX) / 2);
    const snapThreshold = 4;

    const onMove = (moveEvent) => {
      const delta = moveEvent.clientY - startY;
      let newOffset = Math.min(maxOffset, Math.max(-maxOffset, startOffset + delta));

      if (Math.abs(newOffset) < snapThreshold) {
        newOffset = 0;
        this.centerGuide.hidden = false;
      } else {
        this.centerGuide.hidden = true;
      }

      this.verticalOffset = newOffset;
      this.applyVerticalOffset();
    };
    const onUp = () => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      this.centerGuide.hidden = true;
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp, { once: true });
  }

  applyVerticalOffset() {
    this.previewLines.style.transform = `translateY(calc(-50% + ${this.verticalOffset}px))`;
  }

  applyFontToLine(editable) {
    const option = this.fontSelect.options[this.fontSelect.selectedIndex];
    editable.style.fontFamily = option.value;
    editable.style.fontStyle = option.dataset.previewStyle || 'normal';
  }

  applyFontToAllLines() {
    this.previewLines
      .querySelectorAll('.card-configurator__line-text')
      .forEach((editable) => this.applyFontToLine(editable));
  }

  handleLineKeydown(event, editable) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.splitLine(editable);
    } else if (event.key === 'Backspace') {
      const selection = window.getSelection();
      const atStart = selection.isCollapsed && this.getCaretOffset(editable) === 0;
      if (atStart) {
        const wrapper = editable.closest('.card-configurator__line');
        const prevWrapper = wrapper.previousElementSibling;
        if (prevWrapper) {
          event.preventDefault();
          this.mergeLineIntoPrevious(wrapper, prevWrapper);
        }
      }
    }
  }

  handleLinePaste(event) {
    event.preventDefault();
    const text = (event.clipboardData || window.clipboardData).getData('text/plain');
    document.execCommand('insertText', false, text);
  }

  handleLineWheel(event, editable) {
    event.preventDefault();
    const current = Number(editable.dataset.size) || DEFAULT_LINE_SIZE;
    const next = Math.min(MAX_LINE_SIZE, Math.max(MIN_LINE_SIZE, current + (event.deltaY < 0 ? 1 : -1)));
    this.setLineSize(editable, next);
  }

  handleCornerDrag(event, editable) {
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget;
    const startY = event.clientY;
    const startSize = Number(editable.dataset.size) || DEFAULT_LINE_SIZE;
    handle.setPointerCapture(event.pointerId);

    const onMove = (moveEvent) => {
      const delta = startY - moveEvent.clientY;
      const newSize = Math.min(
        MAX_LINE_SIZE,
        Math.max(MIN_LINE_SIZE, Math.round(startSize + delta / DRAG_SENSITIVITY))
      );
      this.setLineSize(editable, newSize);
    };
    const onUp = () => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp, { once: true });
  }

  handleRotateDrag(event, wrapper, editable) {
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    const rect = wrapper.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const onMove = (moveEvent) => {
      const angle = Math.atan2(moveEvent.clientY - centerY, moveEvent.clientX - centerX);
      const degrees = Math.round(angle * (180 / Math.PI) + 90);
      editable.dataset.rotation = String(degrees);
      editable.style.transform = `rotate(${degrees}deg)`;
    };
    const onUp = () => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp, { once: true });
  }

  setLineSize(editable, size) {
    editable.dataset.size = String(size);
    editable.style.fontSize = `${size}px`;
    this.checkOverflow();
  }

  getCaretOffset(editable) {
    const selection = window.getSelection();
    if (!selection.rangeCount) return editable.textContent.length;
    const range = selection.getRangeAt(0);
    if (!editable.contains(range.startContainer)) return editable.textContent.length;
    const preRange = range.cloneRange();
    preRange.selectNodeContents(editable);
    preRange.setEnd(range.startContainer, range.startOffset);
    return preRange.toString().length;
  }

  focusLineAt(editable, offset) {
    editable.focus();
    const selection = window.getSelection();
    const range = document.createRange();
    if (editable.childNodes.length === 0) {
      range.selectNodeContents(editable);
      range.collapse(true);
    } else {
      const textNode = editable.firstChild;
      const max = textNode.textContent ? textNode.textContent.length : 0;
      range.setStart(textNode, Math.min(offset, max));
      range.collapse(true);
    }
    selection.removeAllRanges();
    selection.addRange(range);
  }

  splitLine(editable) {
    const text = editable.textContent;
    const offset = this.getCaretOffset(editable);
    const before = text.slice(0, offset);
    const after = text.slice(offset);
    editable.textContent = before;

    const size = Number(editable.dataset.size) || DEFAULT_LINE_SIZE;
    const wrapper = editable.closest('.card-configurator__line');
    const newWrapper = this.createLine(after, size);
    wrapper.after(newWrapper);

    const newEditable = newWrapper.querySelector('.card-configurator__line-text');
    this.focusLineAt(newEditable, 0);
    this.checkOverflow();
  }

  mergeLineIntoPrevious(wrapper, prevWrapper) {
    const editable = wrapper.querySelector('.card-configurator__line-text');
    const prevEditable = prevWrapper.querySelector('.card-configurator__line-text');
    const mergeOffset = prevEditable.textContent.length;
    prevEditable.textContent = prevEditable.textContent + editable.textContent;
    wrapper.remove();
    this.focusLineAt(prevEditable, mergeOffset);
    this.checkOverflow();
  }

  getLines() {
    return Array.from(this.previewLines.querySelectorAll('.card-configurator__line-text')).map(
      (editable) => editable.textContent
    );
  }

  getLineSizes() {
    return Array.from(this.previewLines.querySelectorAll('.card-configurator__line-text')).map(
      (editable) => Number(editable.dataset.size) || DEFAULT_LINE_SIZE
    );
  }

  getLineColors() {
    return Array.from(this.previewLines.querySelectorAll('.card-configurator__line-text')).map(
      (editable) => editable.dataset.color || '#2c2c2a'
    );
  }

  getLineRotations() {
    return Array.from(this.previewLines.querySelectorAll('.card-configurator__line-text')).map(
      (editable) => Number(editable.dataset.rotation) || 0
    );
  }

  checkOverflow() {
    requestAnimationFrame(() => {
      const availableHeight = this.previewCard.clientHeight - PREVIEW_PADDING_PX;
      const contentHeight = this.previewLines.scrollHeight;
      this.overflowing = contentHeight > availableHeight;
      this.overflowWarning.hidden = !this.overflowing;
    });
  }

  // --- Bevestigen en versturen ---

  async handleConfirm() {
    const selectedDesign = this.designRadios.find((radio) => radio.checked);
    const lines = this.getLines();
    const message = lines.join('\n');
    const selectedFont = this.fontSelect.options[this.fontSelect.selectedIndex];

    if (!selectedDesign || !message.trim()) {
      this.errorEl.hidden = false;
      return;
    }

    if (this.overflowing) {
      this.overflowWarning.hidden = false;
      return;
    }

    if (!this.kaartjeVariantId) {
      this.errorEl.textContent =
        'Er is nog geen kaartje-product ingesteld. Kies het in de blok-instellingen van de theme-editor.';
      this.errorEl.hidden = false;
      return;
    }

    const lineSizes = this.getLineSizes();
    const lineColors = this.getLineColors();
    const lineRotations = this.getLineRotations();

    const properties = {};
    properties[this.propertyDesign] = selectedDesign.value;
        if (selectedDesign.dataset.designImage) {
      properties['_Kaartje ontwerp afbeelding'] = selectedDesign.dataset.designImage;
    }
    properties[this.propertyMessage] = message;
    properties[this.propertyFont] = selectedFont.textContent.trim();
    properties[this.propertyLineSizes] = lineSizes.map((size) => `${size}px`).join(', ');
    properties[this.propertyLineColors] = lineColors.join(', ');
    properties[this.propertyLineRotations] = lineRotations.map((deg) => `${deg}°`).join(', ');
    properties[this.propertyVerticalOffset] = `${Math.round(this.verticalOffset)}px`;
    if (this.propertyLink && this.productTitle) {
      properties[this.propertyLink] = this.productTitle;
    }
    const linkId = Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    properties['_Kaartje koppeling'] = linkId;


    const confirmButton = this.querySelector('[data-action="confirm"]');
    const originalLabel = confirmButton.textContent;
    confirmButton.disabled = true;
    confirmButton.textContent = 'Kaartje toevoegen…';

    let previewBlob = null;
    try {
            previewBlob = await this.generatePreviewImage(
        lines,
        lineSizes,
        lineColors,
        lineRotations,
        this.verticalOffset,
        selectedDesign.dataset.designImage
      );
    } catch (error) {
      // Als de voorbeeldafbeelding onverwacht niet gegenereerd kan worden,
      // blokkeren we de bestelling niet. De tekstuele eigenschappen gaan
      // hieronder alsnog mee.
    }

    // Voeg eerst het losse "Kaartje"-product (met meerprijs) toe aan de
    // winkelwagen, inclusief het gekozen ontwerp/tekst en de
    // voorbeeldafbeelding als line item properties.
    try {
      await this.addKaartjeToCart(properties, previewBlob);
    } catch (error) {
      confirmButton.disabled = false;
      confirmButton.textContent = originalLabel;
      this.errorEl.textContent =
        'Het kaartje kon niet aan de winkelwagen worden toegevoegd. Probeer het opnieuw.';
      this.errorEl.hidden = false;
      return;
    }

    // Zet een markering op het hoofdproduct zodat in de winkelwagen/bestelling
    // duidelijk is dat er een kaartje bij hoort.
    if (this.mainMarkProperty) {
      this.setHiddenProperty(this.mainMarkProperty, this.mainMarkValue || 'Ja');
    }
        this.setHiddenProperty('_Kaartje koppeling', linkId);

    confirmButton.disabled = false;
    confirmButton.textContent = originalLabel;

    this.configured = true;
    this.dialog.close();

    // Voeg daarna het hoofdproduct toe via de bestaande "Toevoegen aan
    // winkelwagen"-knop van het thema zelf. De cart-refresh van het thema
    // haalt de volledige winkelwagen op, dus het zojuist toegevoegde kaartje
    // verschijnt vanzelf mee in de cart-drawer.
    if (this.submitButton) {
      this.submitButton.click();
    }
  }

  /**
   * Voegt het losse kaartje-product toe aan de winkelwagen via de AJAX cart API.
   * @param {Record<string, string>} properties
   * @param {Blob|null} previewBlob
   */
  async addKaartjeToCart(properties, previewBlob) {
    const url =
      (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) ||
      (window.Shopify && Shopify.routes && Shopify.routes.root
        ? `${Shopify.routes.root}cart/add.js`
        : '/cart/add.js');

    const formData = new FormData();
    formData.append('id', this.kaartjeVariantId);
    formData.append('quantity', '1');

    Object.keys(properties).forEach((name) => {
      formData.append(`properties[${name}]`, properties[name]);
    });

    if (previewBlob) {
      formData.append(
        `properties[_${this.propertyPreviewImage}]`,
        new File([previewBlob], 'kaartje-voorbeeld.png', { type: 'image/png' })
      );
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Kaartje toevoegen mislukte (status ${response.status})`);
    }

    return response.json();
  }

  removeMainMark() {
    if (!this.mainMarkProperty || !this.form) return;
    const inputName = `properties[${this.mainMarkProperty}]`;
    const input = this.form.querySelector(`input[name="${CSS.escape(inputName)}"]`);
    if (input) input.remove();
  }

  async generatePreviewImage(lines, lineSizes, lineColors, lineRotations, verticalOffset, designImageUrl) {
    const scale = 3.5;
    const width = Math.round(220 * scale);
    const height = Math.round((width * 148) / 105);
    const padding = Math.round(18 * scale);
    const offsetPx = (verticalOffset || 0) * scale;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.textAlign = 'center';
   

    const option = this.fontSelect.options[this.fontSelect.selectedIndex];
    const fontFamily = option.value;
    const isItalic = (option.dataset.previewStyle || 'normal') === 'italic';
    const primaryFamily = fontFamily.split(',')[0].replace(/['"]/g, '');

    await Promise.all(
      lineSizes.map((size) =>
        document.fonts
          .load(`${isItalic ? 'italic ' : ''}${Math.round(size * scale)}px "${primaryFamily}"`)
          .catch(() => {})
      )
    );

    ctx.textAlign = 'center';

    const lineHeights = lineSizes.map((size) => size * scale * 1.3);
    const totalHeight = lineHeights.reduce((sum, h) => sum + h, 0);
    let cursorY = Math.max(padding, (height - totalHeight) / 2 + offsetPx);

    lines.forEach((line, index) => {
      const size = (lineSizes[index] || DEFAULT_LINE_SIZE) * scale;
      const color = lineColors[index] || '#2c2c2a';
      const rotation = ((lineRotations[index] || 0) * Math.PI) / 180;
      const baselineY = cursorY + size * 0.85;

      ctx.save();
      ctx.translate(width / 2, baselineY);
      ctx.rotate(rotation);
      ctx.fillStyle = color;
      ctx.font = `${isItalic ? 'italic ' : ''}${size}px "${primaryFamily}"`;
      ctx.fillText(line || '\u00A0', 0, 0);
      ctx.restore();

      cursorY += lineHeights[index];
    });

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('canvas.toBlob gaf geen resultaat'));
        }
      }, 'image/png');
    });
  }

  setHiddenProperty(propertyName, value) {
    if (!this.form) return;
    const inputName = `properties[${propertyName}]`;
    let input = this.form.querySelector(`input[name="${CSS.escape(inputName)}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = inputName;
      this.form.appendChild(input);
    }
    input.value = value;
  }
}

customElements.define('card-configurator', CardConfigurator);